import { LanguageType } from 'types';
import { RowErrorDetail } from './parseErrors';
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell
} from 'features/components/Table';
import { CODELIST_SIDEBAR_DICTIONARY } from 'features/codelist/sidebar/Dictionary';

type ValidationErrorTableProps = {
	errorDetails: RowErrorDetail[];
	language: LanguageType;
	maxDisplayRows?: number;
};

export const ValidationErrorTable = ({
	errorDetails,
	language,
	maxDisplayRows = 100
}: ValidationErrorTableProps) => {
	const translation = DICTIONARY[language];
	const fieldTranslations = CODELIST_SIDEBAR_DICTIONARY[language];

	const displayedErrors = errorDetails.slice(0, maxDisplayRows);

	return (
		<div className="bg-error-700/10 border border-error-700 rounded-md px-6 py-4 mt-4">
			<h3 className="text-lg text-error-700 font-bold">{translation.title}</h3>

			<div className="mt-4 max-h-[400px] overflow-y-auto">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="font-mono">{translation.headers.row}</TableHead>
							<TableHead>{translation.headers.id}</TableHead>
							<TableHead>{translation.headers.field}</TableHead>
							<TableHead>{translation.headers.error}</TableHead>
							<TableHead>{translation.headers.value}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{displayedErrors.map((errorDetail, index) => {
							const fieldLabel =
								fieldTranslations[errorDetail.fieldName]?.label ||
								errorDetail.fieldName;
							const errorLabel = translation.errorTypes[errorDetail.errorType];
							const displayValue = errorDetail.fieldValue || translation.emptyValue;

							return (
								<TableRow key={index}>
									<TableCell className="font-mono">
										{errorDetail.rowNumber}
									</TableCell>
									<TableCell
										className="max-w-[150px] truncate"
										title={errorDetail.customId}
									>
										{errorDetail.customId || translation.emptyValue}
									</TableCell>
									<TableCell>{fieldLabel}</TableCell>
									<TableCell>{errorLabel}</TableCell>
									<TableCell
										className="font-mono max-w-[200px] truncate"
										title={displayValue}
									>
										{displayValue}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</div>
		</div>
	);
};

const DICTIONARY = {
	[LanguageType.English]: {
		headers: {
			row: 'Row',
			id: 'ID',
			field: 'Field',
			error: 'Error',
			value: 'Value'
		},
		errorTypes: {
			'invalid-phone': 'Invalid phone number',
			'unique-conflict': 'Duplicate ID',
			'invalid-email': 'Invalid email',
			'invalid-date-of-birth': 'Invalid date of birth',
			'missing-custom-id': 'Missing ID'
		},
		errorDescriptions: {
			'invalid-phone':
				'Invalid phone number. All phone numbers must start with a country code (e.g. +47).',
			'unique-conflict':
				'Duplicate ID. Make sure all rows in the codelist you are importing have unique IDs.',
			'invalid-email': 'Invalid email address(es).',
			'invalid-date-of-birth': 'Invalid date of birth(s).',
			'missing-custom-id':
				'Missing codelist ID. All rows must have an ID value in the selected ID column.'
		},
		showingFirst: (displayed: number, total: number) =>
			`Showing first ${displayed} of ${total} errors`,
		emptyValue: '(empty)',
		title: 'Validation errors',
		description: 'Please fix the errors in your uploaded file and upload it again.'
	},
	[LanguageType.Norwegian]: {
		headers: {
			row: 'Rad',
			id: 'ID',
			field: 'Felt',
			error: 'Feil',
			value: 'Verdi'
		},
		errorTypes: {
			'invalid-phone': 'Ugyldig telefonnummer',
			'unique-conflict': 'Duplikat ID',
			'invalid-email': 'Ugyldig e-post',
			'invalid-date-of-birth': 'Ugyldig fødselsdato',
			'missing-custom-id': 'Manglende ID'
		},
		errorDescriptions: {
			'invalid-phone':
				'Ugyldig telefonnummer. Alle telefonnummer må starte med landskode (f.eks. +47).',
			'unique-conflict':
				'Duplikat ID. Pass på at alle radene i kodelisten du importerer har unike ID-er.',
			'invalid-email': 'Ugyldig e-postadresse(r).',
			'invalid-date-of-birth': 'Ugyldig fødselsdato(er).',
			'missing-custom-id':
				'Manglende kodeliste-ID. Alle rader må ha en ID-verdi i den valgte ID-kolonnen.'
		},
		showingFirst: (displayed: number, total: number) =>
			`Viser første ${displayed} av ${total} feil`,
		emptyValue: '(tom)',
		title: 'Valideringsfeil',
		description: 'Vennligst fiks feilene i den importerte filen og last den opp igjen.'
	}
} as const;
